import React from "react";

import { Target, ChevronDown } from "lucide-react";

import {
  ExperimentType,
  contentByExperimentType,
} from "./researchFoundation.constants";

interface SuccessSignals {
  qualitative: string;
  quantitative: string;
}

interface SuccessSignalsSectionProps {
  signals: SuccessSignals;
  onChange: (signals: SuccessSignals) => void;
  experimentType: ExperimentType;
  defaultOpen?: boolean;
}

export const SuccessSignalsSection: React.FC<SuccessSignalsSectionProps> = ({
  signals,
  onChange,
  experimentType,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const config = contentByExperimentType[experimentType];

  const filledCount = [signals.qualitative, signals.quantitative].filter((s) =>
    s.trim(),
  ).length;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        className={`w-full rounded-lg border p-4 transition-all duration-300 ${
          isOpen
            ? "border-amber-400/30 bg-amber-400/5"
            : "border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target size={16} className="text-blue-400" />
            <span className="text-sm font-semibold text-white">
              Success Signals
            </span>
            <span className="text-xs text-white/40">({filledCount}/2)</span>
          </div>
          <ChevronDown
            size={16}
            className={`text-white/60 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="space-y-6 pt-4">
          <p className="text-xs text-white/50">
            How will you know if your experiment succeeded? Define both
            qualitative and quantitative indicators.
          </p>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-400/60" />
                <span className="text-sm font-medium text-white/80">
                  Qualitative Signals
                </span>
              </div>
            </div>
            <textarea
              value={signals.qualitative}
              onChange={(e) =>
                onChange({ ...signals, qualitative: e.target.value })
              }
              rows={2}
              placeholder={config.successSignals.qualitative.placeholder}
              className="w-full resize-none rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-amber-400/50 focus:outline-none"
            />
            <p className="text-[10px] text-white/40">
              Observable behaviors, user feedback patterns, sentiment indicators
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-400/60" />
                <span className="text-sm font-medium text-white/80">
                  Quantitative Signals
                </span>
              </div>
            </div>
            <textarea
              value={signals.quantitative}
              onChange={(e) =>
                onChange({ ...signals, quantitative: e.target.value })
              }
              rows={2}
              placeholder={config.successSignals.quantitative.placeholder}
              className="w-full resize-none rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-amber-400/50 focus:outline-none"
            />
            <p className="text-[10px] text-white/40">
              Measurable metrics, thresholds, statistical significance
              requirements
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
